import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProfile } from '../../store/slices/profileSlice';
import { createClass, getClassById, updateClass } from '../../api/classes';
import { getLanguages } from '../../api/profile';
import { searchStudentsApi } from '../../api/events';
import './CreateClass.scss';

export const CreateClass = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEditMode = !!id;

  const { isAuthenticated } = useSelector((state) => state.auth);
  const { profile, loading: profileLoading, fetched: profileFetched, error: profileError } = useSelector((state) => state.profile);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [languages, setLanguages] = useState([]);

  // Students search & selection state (for Private visibility)
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentSearchResults, setStudentSearchResults] = useState([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    language: '',
    type: 'group',
    is_paid: 'free',
    class_fees: '',
    max_participants: '',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '10:00',
    joinLink: '',
    slot_duration: '0.5',
    is_recurrence: false,
    repeat_every: '1',
    repeat_type: 'week',
    end_type: 'never',
    recurrence_end_date: '',
    occurrences: '1',
    class_visibility: 'public',
    post_to_marketplace: false,
  });

  const [keypoints, setKeypoints] = useState(['']);
  const [selectedDays, setSelectedDays] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Authenticate user & load profile
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: window.location.pathname } });
    } else if (!profile && !profileLoading?.profile && !profileFetched && !profileError?.profile) {
      dispatch(fetchProfile());
    }
  }, [isAuthenticated, profile, profileLoading, profileFetched, profileError, dispatch, navigate]);

  // Load languages and class details if editing
  useEffect(() => {
    const fetchLanguagesList = async () => {
      try {
        const response = await getLanguages();
        if (response && response.data) {
          setLanguages(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch languages:', err);
      }
    };
    fetchLanguagesList();

    if (isEditMode) {
      const fetchClassDetails = async () => {
        try {
          const response = await getClassById(id);
          if (response && response.data) {
            const cls = response.data;
            
            const formatDateStr = (d) => {
              if (!d) return '';
              const dateObj = new Date(d);
              if (isNaN(dateObj.getTime())) return '';
              const yyyy = dateObj.getFullYear();
              const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
              const dd = String(dateObj.getDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}`;
            };

            const formatTimeStr = (d) => {
              if (!d) return '';
              const dateObj = new Date(d);
              if (isNaN(dateObj.getTime())) return '';
              const hh = String(dateObj.getHours()).padStart(2, '0');
              const mm = String(dateObj.getMinutes()).padStart(2, '0');
              return `${hh}:${mm}`;
            };

            const keypointsArray = Array.isArray(cls.keypoints)
              ? cls.keypoints
              : (cls.keypoints ? cls.keypoints.split(',').map(k => k.trim()) : ['']);

            setKeypoints(keypointsArray.length > 0 ? keypointsArray : ['']);
            setSelectedDays(cls.selectedDays || cls.selected_days || []);

            setFormData({
              title: cls.title || '',
              description: cls.description || '',
              language: cls.language?._id || cls.language || '',
              type: cls.type || 'group',
              is_paid: cls.is_paid || 'free',
              class_fees: cls.class_fees || '',
              max_participants: cls.maxParticipants || cls.max_participants || '',
              start_date: formatDateStr(cls.start_date),
              end_date: formatDateStr(cls.end_date),
              start_time: cls.start_time || formatTimeStr(cls.start_date) || '09:00',
              end_time: cls.end_time || formatTimeStr(cls.end_date) || '10:00',
              joinLink: cls.joinLink || cls.join_link || '',
              slot_duration: String(cls.slot_duration || cls.duration || '0.5'),
              is_recurrence: !!(cls.isRecurrence || cls.is_recurrence || cls.isRecurring),
              repeat_every: String(cls.repeatEvery || cls.repeat_every || '1'),
              repeat_type: cls.repeatType || cls.repeat_type || 'week',
              end_type: cls.endType || cls.end_type || 'never',
              recurrence_end_date: formatDateStr(cls.recurrenceEndDate || cls.recurrence_end_date),
              occurrences: String(cls.occurrences || '1'),
              class_visibility: cls.access || cls.class_visibility || 'public',
              post_to_marketplace: !!(cls.post_to_marketplace || cls.postToMarketplace),
            });

            if (cls.cover_image) {
              setImagePreview(cls.cover_image);
            }

            if (cls.students && Array.isArray(cls.students)) {
              const formattedStudents = cls.students.map(s => {
                const user = s.user_id;
                return typeof user === 'object' && user !== null
                  ? { _id: user._id, full_name: user.full_name, email: user.email, profile_picture: user.profile_picture }
                  : { _id: user, full_name: 'Yoospeak Student' };
              });
              setSelectedStudents(formattedStudents);
            }
          }
        } catch (err) {
          setError('Failed to load class details.');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchClassDetails();
    }
  }, [id, isEditMode]);

  // Handle student search
  useEffect(() => {
    if (!studentSearchQuery.trim()) {
      setStudentSearchResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setSearchingStudents(true);
      try {
        const response = await searchStudentsApi({ search: studentSearchQuery });
        if (response && response.data) {
          setStudentSearchResults(response.data);
        }
      } catch (err) {
        console.error('Failed to search students:', err);
      } finally {
        setSearchingStudents(false);
      }
    }, 450);

    return () => clearTimeout(delayDebounceFn);
  }, [studentSearchQuery]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddKeypoint = () => {
    setKeypoints([...keypoints, '']);
  };

  const handleRemoveKeypoint = (index) => {
    setKeypoints(keypoints.filter((_, i) => i !== index));
  };

  const handleKeypointChange = (index, val) => {
    const updated = [...keypoints];
    updated[index] = val;
    setKeypoints(updated);
  };

  const handleSelectStudent = (student) => {
    if (!selectedStudents.some(s => s._id === student._id)) {
      setSelectedStudents([...selectedStudents, student]);
    }
    setStudentSearchQuery('');
    setStudentSearchResults([]);
  };

  const handleRemoveStudent = (studentId) => {
    setSelectedStudents(selectedStudents.filter(s => s._id !== studentId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.start_date || !formData.end_date) {
      setError('Start date and end date are required.');
      setLoading(false);
      return;
    }

    if (formData.class_visibility === 'private' && selectedStudents.length === 0) {
      setError('Please assign at least one student for a private class.');
      setLoading(false);
      return;
    }

    if (formData.is_paid === 'paid' && !formData.class_fees) {
      setError('Please specify the fee for this paid class.');
      setLoading(false);
      return;
    }

    if (!formData.language) {
      setError('Please select a language for the class.');
      setLoading(false);
      return;
    }

    try {
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);

      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('language', formData.language);
      data.append('type', formData.max_participants === '1' ? 'one_to_one' : 'group');
      data.append('is_paid', formData.is_paid);
      data.append('start_date', startDateTime.toISOString());
      data.append('end_date', endDateTime.toISOString());
      data.append('start_time', formData.start_time);
      data.append('end_time', formData.end_time);

      const filteredKeypoints = keypoints.filter(k => k.trim() !== '');
      data.append('keypoints', filteredKeypoints.join(', '));

      if (coverImage) {
        data.append('coverImageUrl', coverImage);
      }

      if (formData.is_paid === 'paid') {
        data.append('class_fees', formData.class_fees);
      }

      if (formData.max_participants) {
        data.append('max_participants', formData.max_participants);
      }

      const studentIds = selectedStudents.map(s => s._id);
      data.append('students', JSON.stringify(studentIds));

      data.append('joinLink', formData.joinLink);
      data.append('join_link', formData.joinLink);
      data.append('slot_duration', formData.slot_duration);
      data.append('isRecurrence', String(formData.is_recurrence));
      data.append('is_recurrence', String(formData.is_recurrence));
      data.append('repeatEvery', formData.repeat_every);
      data.append('repeatType', formData.repeat_type);
      data.append('selectedDays', JSON.stringify(selectedDays));
      data.append('endType', formData.end_type);

      if (formData.end_type === 'onDate' && formData.recurrence_end_date) {
        const recEnd = new Date(`${formData.recurrence_end_date}T23:59:59`);
        data.append('recurrenceEndDate', recEnd.toISOString());
      }
      data.append('occurrences', formData.occurrences);
      data.append('access', formData.class_visibility);
      data.append('class_visibility', formData.class_visibility);
      data.append('post_to_marketplace', String(formData.post_to_marketplace));
      data.append('postToMarketplace', String(formData.post_to_marketplace));

      if (isEditMode) {
        await updateClass(id, data);
      } else {
        await createClass(data);
      }

      navigate('/classes');
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please check your inputs and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="create-class-page d-flex align-items-center justify-content-center">
        <Spinner animation="border" color="primary" />
      </div>
    );
  }

  return (
    <div className="create-class-page">
      <Container>
        <div className="form-container">
          <div className="step-header">
            <Link to="#" onClick={(e) => { e.preventDefault(); navigate(-1); }} className="back-btn">
              <i className="bi bi-chevron-left fs-5"></i> Back
            </Link>
            <h1>Schedule Class</h1>
          </div>

          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            {/* Class Title */}
            <Form.Group className="mb-4">
              <Form.Label>Class Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                placeholder="e.g. Intermediate English Conversation"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            {/* Class Description */}
            <Form.Group className="mb-4">
              <Form.Label>Class Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                rows={4}
                placeholder="Describe what students will learn in this class..."
                value={formData.description}
                onChange={handleInputChange}
              />
            </Form.Group>

            {/* Upload Class Cover */}
            <Form.Group className="mb-4">
              <Form.Label>Upload Class Cover</Form.Label>
              <div className="image-upload-wrapper">
                <label htmlFor="coverImageInput" className="upload-placeholder">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" />
                  ) : (
                    <>
                      <i className="bi bi-image placeholder-icon"></i>
                      <span>Click to upload cover image</span>
                    </>
                  )}
                  <input
                    type="file"
                    id="coverImageInput"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                  />
                </label>
              </div>
            </Form.Group>

            {/* Key Points */}
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Form.Label className="mb-0">Key Points</Form.Label>
                <button
                  type="button"
                  className="add-keypoint-btn border-0 bg-transparent text-primary-purple fw-semibold d-flex align-items-center gap-1"
                  onClick={handleAddKeypoint}
                >
                  <i className="bi bi-plus-circle-fill"></i> Add Key Point
                </button>
              </div>
              <div className="d-flex flex-column gap-2">
                {keypoints.map((kp, idx) => (
                  <div key={idx} className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="text"
                      placeholder={`Enter key point ${idx + 1}`}
                      value={kp}
                      onChange={(e) => handleKeypointChange(idx, e.target.value)}
                    />
                    {keypoints.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove-keypoint text-muted border-0 bg-transparent"
                        onClick={() => handleRemoveKeypoint(idx)}
                      >
                        <i className="bi bi-x-circle fs-5"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Language Selection */}
            <Form.Group className="mb-4">
              <Form.Label>Class Language</Form.Label>
              <Form.Select
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Language</option>
                {languages.map((lang) => (
                  <option key={lang._id} value={lang._id}>
                    {lang.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Dates Row */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Select Start Date</Form.Label>
                  <div className="input-with-icon">
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Select End Date</Form.Label>
                  <div className="input-with-icon">
                    <Form.Control
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Times Row */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Select Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Select End Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Class Link */}
            <Form.Group className="mb-4">
              <Form.Label>Class Link</Form.Label>
              <Form.Control
                type="text"
                name="joinLink"
                placeholder="Enter class link (Google Meet, Zoom, etc.)"
                value={formData.joinLink}
                onChange={handleInputChange}
              />
            </Form.Group>

            {/* Slot Duration & Max Seats Row */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Slot Duration</Form.Label>
                  <Form.Select
                    name="slot_duration"
                    value={formData.slot_duration}
                    onChange={handleInputChange}
                  >
                    <option value="0.5">0.5 hour</option>
                    <option value="1">1 hour</option>
                    <option value="1.5">1.5 hours</option>
                    <option value="2">2 hours</option>
                    <option value="2.5">2.5 hours</option>
                    <option value="3">3 hours</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Maximum Seats</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_participants"
                    placeholder="Enter maximum number of students"
                    value={formData.max_participants}
                    onChange={handleInputChange}
                    min="1"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Recurring Class Checkbox */}
            <Form.Group className="mb-4 d-flex align-items-center justify-content-between p-3 border rounded-3 bg-light transition-all">
              <div>
                <Form.Label className="mb-0 fw-semibold text-dark">Recurring Class</Form.Label>
              </div>
              <Form.Check
                type="checkbox"
                name="is_recurrence"
                checked={formData.is_recurrence}
                onChange={handleInputChange}
                className="fs-4"
              />
            </Form.Group>

            {/* Recurring Settings Container */}
            {formData.is_recurrence && (
              <div className="recurring-settings-panel border rounded-3 p-3 mb-4 bg-light">
                <h5 className="panel-title d-flex align-items-center gap-2 mb-3 text-navy font-poppins">
                  <i className="bi bi-arrow-repeat fs-5"></i> Recurring Settings
                </h5>

                {/* Repeat Every Row */}
                <Row className="mb-3 align-items-end">
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label>Repeat Every</Form.Label>
                      <Form.Control
                        type="number"
                        name="repeat_every"
                        value={formData.repeat_every}
                        onChange={handleInputChange}
                        min="1"
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Select
                        name="repeat_type"
                        value={formData.repeat_type}
                        onChange={handleInputChange}
                      >
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Repeat On Days */}
                {formData.repeat_type === 'week' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Repeat on</Form.Label>
                    <div className="day-selectors-row d-flex gap-2 flex-wrap">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
                        const isSelected = selectedDays.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            className={`day-selector-btn ${isSelected ? 'active' : ''}`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedDays(selectedDays.filter(d => d !== day));
                              } else {
                                setSelectedDays([...selectedDays, day]);
                              }
                            }}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </Form.Group>
                )}

                {/* End Recurrence */}
                <Form.Group className="mb-3">
                  <Form.Label className="d-block">End</Form.Label>
                  <div className="d-flex flex-column gap-2 mt-1">
                    <Form.Check
                      type="radio"
                      id="end_never"
                      label="Never"
                      name="end_type"
                      value="never"
                      checked={formData.end_type === 'never'}
                      onChange={handleInputChange}
                    />
                    <Form.Check
                      type="radio"
                      id="end_date"
                      label="On specific date"
                      name="end_type"
                      value="onDate"
                      checked={formData.end_type === 'onDate'}
                      onChange={handleInputChange}
                    />
                    {formData.end_type === 'onDate' && (
                      <div className="ms-4 my-1">
                        <Form.Control
                          type="date"
                          name="recurrence_end_date"
                          value={formData.recurrence_end_date}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    )}
                    <Form.Check
                      type="radio"
                      id="end_occurrences"
                      label="After occurrences"
                      name="end_type"
                      value="afterOccurrences"
                      checked={formData.end_type === 'afterOccurrences'}
                      onChange={handleInputChange}
                    />
                    {formData.end_type === 'afterOccurrences' && (
                      <div className="ms-4 my-1">
                        <Form.Control
                          type="number"
                          name="occurrences"
                          value={formData.occurrences}
                          onChange={handleInputChange}
                          min="1"
                          required
                        />
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            )}

            {/* Payment Type */}
            <div className="mb-4 border rounded-3 p-3 bg-white">
              <Form.Label className="d-block mb-3 fw-semibold text-dark">Payment Type</Form.Label>
              <div className="d-flex flex-column gap-3">
                <label className="d-flex align-items-start gap-3 pointer">
                  <input
                    type="radio"
                    name="is_paid"
                    value="free"
                    checked={formData.is_paid === 'free'}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <div>
                    <span className="fw-semibold text-dark d-block">Free</span>
                    <span className="text-muted text-xs">Anyone can join.</span>
                  </div>
                </label>

                <label className="d-flex align-items-start gap-3 pointer">
                  <input
                    type="radio"
                    name="is_paid"
                    value="paid"
                    checked={formData.is_paid === 'paid'}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <div>
                    <span className="fw-semibold text-dark d-block">One-time Payment</span>
                    <span className="text-muted text-xs">Students pay once to join.</span>
                  </div>
                </label>
              </div>

              {formData.is_paid === 'paid' && (
                <Form.Group className="mt-3">
                  <Form.Label>Class Fees ($)</Form.Label>
                  <Form.Control
                    type="number"
                    name="class_fees"
                    placeholder="Enter fee amount in USD"
                    value={formData.class_fees}
                    onChange={handleInputChange}
                    required
                    min="1"
                  />
                </Form.Group>
              )}
            </div>

            {/* Class Visibility */}
            <div className="mb-4 border rounded-3 p-3 bg-white">
              <Form.Label className="d-block mb-3 fw-semibold text-dark">Class Visibility</Form.Label>
              <div className="d-flex flex-column gap-3 mb-3">
                <label className="d-flex align-items-start gap-3 pointer">
                  <input
                    type="radio"
                    name="class_visibility"
                    value="public"
                    checked={formData.class_visibility === 'public'}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <div>
                    <span className="fw-semibold text-dark d-block">Public</span>
                    <span className="text-muted text-xs">Anyone can join this class.</span>
                  </div>
                </label>

                <label className="d-flex align-items-start gap-3 pointer">
                  <input
                    type="radio"
                    name="class_visibility"
                    value="private"
                    checked={formData.class_visibility === 'private'}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <div>
                    <span className="fw-semibold text-dark d-block">Private</span>
                    <span className="text-muted text-xs">Only selected users can join this class.</span>
                  </div>
                </label>
              </div>

              {/* Private Student Assignment */}
              {formData.class_visibility === 'private' && (
                <div className="student-selection-box p-3 border rounded-3 bg-light mt-3">
                  <Form.Group className="mb-3 position-relative">
                    <Form.Label className="fw-semibold text-sm">Assign Students (Required)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search students by name or email..."
                      value={studentSearchQuery}
                      onChange={(e) => setStudentSearchQuery(e.target.value)}
                    />
                    {searchingStudents && (
                      <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}

                    {studentSearchResults.length > 0 && (
                      <div className="student-search-dropdown shadow rounded-3 border bg-white position-absolute w-100 mt-1 z-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {studentSearchResults.map(student => (
                          <div
                            key={student._id}
                            className="student-search-item d-flex align-items-center gap-3 p-2 pointer hover-bg"
                            onClick={() => handleSelectStudent(student)}
                          >
                            <img
                              src={student.profile_picture || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=32&h=32&fit=crop'}
                              alt={student.full_name}
                              className="rounded-circle"
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                            />
                            <div>
                              <div className="fw-semibold text-sm">{student.full_name}</div>
                              <div className="text-muted text-xs">{student.email}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Form.Group>

                  {/* Selected Students Badges */}
                  <div className="d-flex flex-wrap gap-2">
                    {selectedStudents.map(student => (
                      <Badge
                        key={student._id}
                        bg="purple"
                        className="d-flex align-items-center gap-2 p-2 rounded-pill text-white fw-bold bg-primary-purple"
                        style={{ fontSize: '0.85rem' }}
                      >
                        <span>{student.full_name}</span>
                        <i
                          className="bi bi-x-circle-fill pointer text-white"
                          onClick={() => handleRemoveStudent(student._id)}
                          style={{ fontSize: '1rem' }}
                        />
                      </Badge>
                    ))}
                    {selectedStudents.length === 0 && (
                      <span className="text-muted text-xs">No students assigned yet.</span>
                    )}
                  </div>
                </div>
              )}

              {/* Post to Marketplace Switch */}
              <div className="d-flex align-items-center justify-content-between p-3 border-top mt-3">
                <div style={{ maxWidth: '80%' }}>
                  <span className="fw-semibold text-dark d-block">Post to Marketplace</span>
                  <span className="text-muted text-xs">
                    Enabling this option will publish your class in the Marketplace for marketing and promotional visibility. This allows more students to discover and enroll in your class.
                  </span>
                </div>
                <Form.Check
                  type="switch"
                  id="post_to_marketplace_switch"
                  name="post_to_marketplace"
                  checked={formData.post_to_marketplace}
                  onChange={handleInputChange}
                  className="fs-4"
                />
              </div>
            </div>

            <Button className="btn-submit" type="submit" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : (isEditMode ? 'Update Class' : 'Create Class')}
            </Button>
          </Form>
        </div>
      </Container>
    </div>
  );
};
